/**
 * UI State Atoms
 *
 * Atoms for managing UI state including:
 * - Workflow state machine (audio capture, note generation, etc.)
 * - Sheet/modal visibility
 * - Route tracking
 */
import { atom } from 'jotai';

import type { NoteGenerationStep } from '../components/NoteGenerationLoadingAnimation';
import { VideaNotesUIState, VoiceNotesSource } from '../types';

import { recorderStatusAtom } from './recording';

// ============================================================================
// Base Atoms
// ============================================================================

/** Current UI workflow state */
export const uiStatusAtom = atom<VideaNotesUIState>(
  VideaNotesUIState.AUDIO_CAPTURE,
);

/** Source of where voice notes was triggered from */
export const voiceNotesSourceAtom = atom<VoiceNotesSource>(
  VoiceNotesSource.VOICE_NOTES_BUTTON,
);

/** Whether the recorder sheet/modal is open */
export const isRecorderSheetOpenAtom = atom(false);

/** Flag to reopen sheet after certain operations */
export const shouldReopenRecorderSheetAtom = atom(false);

/** Whether user is on the annotator route */
export const isActiveAnnotatorRouteAtom = atom(false);

/** Whether the cancel recording confirmation dialog is shown */
export const isCancelRecordingConfirmationShownAtom = atom(false);

/** UI state for device selection dropdown */
export const isDeviceSelectionOpenAtom = atom(false);

/** Current note generation step (preparing → uploading → generating → complete) for loading animation */
export const noteGenerationStepAtom = atom<NoteGenerationStep>('preparing');

// ============================================================================
// Derived Atoms
// ============================================================================

/** Whether note generation is in progress */
export const isNoteGenerationActiveAtom = atom((get) => {
  const uiStatus = get(uiStatusAtom);
  return uiStatus === VideaNotesUIState.NOTE_GENERATION;
});

/**
 * Comprehensive recording state check.
 * Returns true if any recording-related activity is happening.
 */
export const isRecordingOrGeneratingAtom = atom((get) => {
  const status = get(recorderStatusAtom);
  const uiStatus = get(uiStatusAtom);
  return (
    status === 'recording' ||
    status === 'paused' ||
    uiStatus === VideaNotesUIState.NOTE_GENERATION
  );
});
